import { useCallback, useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useMatches, createFileRoute, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import {
  AlertTriangle,
  Ban,
  Bell,
  Calendar as CalendarIcon,
  CreditCard,
  Globe,
  Key,
  Loader2,
  Minus,
  Plus,
  ShieldCheck,
  Users,
} from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { ApiKeysClient } from "@/components/account/api-keys-client";
import { NotificationSettingsClient } from "@/components/account/notification-settings-client";
import { AffiliationsSettingsEntry } from "@/components/account/provider/affiliations-settings-entry";
import { ProviderBookingWidgetEntry } from "@/components/account/provider/booking-widget-entry";
import { ProviderCalendarConnectionsPanel } from "@/components/account/provider/calendar-connections-panel";
import { ConsultationPaymentsList } from "@/components/account/provider/consultation-payments-list";
import { useProviderCurrency } from "@/components/account/provider/use-provider-currency";
import { LoadErrorState } from "@/components/ui/load-error-state";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";
import { useApiErrorMessage } from "@/lib/api-error-message";
import { formatMoney } from "@/lib/format-money";
import { PROVIDER_CATEGORIES } from "@/lib/notifications/provider-categories";
import { sidebarDataQueryOptions } from "@/lib/queries/sidebar-data";
import { showToast } from "@/lib/ui/toast";

import { ProviderPaymentsPanel } from "./payments";

type TabKey =
  | "booking-policy"
  | "calendar"
  | "notifications"
  | "api"
  | "payments"
  | "affiliations"
  | "widget";

const VALID_TABS: readonly TabKey[] = [
  "booking-policy",
  "calendar",
  "notifications",
  "api",
  "payments",
  "affiliations",
  "widget",
] as const;

interface SettingsSearch {
  tab: TabKey;
}

export const Route = createFileRoute("/_authenticated/account/provider/settings")({
  validateSearch: (search: Record<string, unknown>): SettingsSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    return {
      tab: VALID_TABS.includes(raw as TabKey) ? (raw as TabKey) : "booking-policy",
    };
  },
  component: SettingsRouteShell,
});

// Child routes (merge, profile, calendar, notifications) are standalone
// pages, mirroring legacy Next.js where each was its own page — when one
// matches, render it instead of the settings hub.
function SettingsRouteShell() {
  const matches = useMatches();
  const hasChild = matches.some((m) => m.routeId.startsWith(`${Route.id}/`));
  if (hasChild) return <Outlet />;
  return <ProviderSettingsPage />;
}

function ProviderSettingsPage() {
  const { t } = useLingui();
  const { tab: activeTab } = Route.useSearch();
  const navigate = useNavigate();
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  // Destructive count badge on the Affiliations tab (legacy
  // provider-settings-client.tsx). sidebar-data is already fetched by the
  // layout chrome + invalidated on `account-state-changed`, so this query
  // resolves from cache.
  const { data: sidebarData } = useQuery(sidebarDataQueryOptions);
  const pendingAffiliations = sidebarData?.pendingAffiliationsCount ?? 0;

  const setActiveTab = useCallback(
    (key: TabKey) => navigate({ to: "/account/provider/settings", search: { tab: key } }),
    [navigate],
  );

  const tabs: TabItem<TabKey>[] = [
    {
      id: "booking-policy",
      label: t`Booking policy`,
      icon: <ShieldCheck className="size-4" />,
    },
    { id: "calendar", label: t`Calendar sync`, icon: <CalendarIcon className="size-4" /> },
    { id: "notifications", label: t`Notifications`, icon: <Bell className="size-4" /> },
    { id: "api", label: t`API keys`, icon: <Key className="size-4" /> },
    { id: "payments", label: t`Payments`, icon: <CreditCard className="size-4" /> },
    {
      id: "affiliations",
      label: t`Affiliations`,
      icon: <Users className="size-4" />,
      trailing:
        pendingAffiliations > 0 ? (
          <span className="bg-destructive text-destructive-foreground flex min-w-[18px] items-center justify-center rounded-full px-1 py-0.5 text-[0.625rem] font-bold">
            {pendingAffiliations > 99 ? "99+" : pendingAffiliations}
          </span>
        ) : undefined,
    },
    { id: "widget", label: t`Booking widget`, icon: <Globe className="size-4" /> },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <header>
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          <Trans>Settings</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>No-show shield, calendar sync, payments, affiliations, and more.</Trans>
        </p>
      </header>

      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
      />

      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={activeTab}
        tabIds={VALID_TABS}
        onTabChange={setActiveTab}
        renderPanel={(panelKey) => {
          let inner: React.ReactNode;
          if (panelKey === "booking-policy") inner = <BookingPolicyPanel />;
          else if (panelKey === "calendar") inner = <ProviderCalendarConnectionsPanel />;
          else if (panelKey === "api") inner = <ApiKeysClient />;
          else if (panelKey === "widget") inner = <ProviderBookingWidgetEntry />;
          else if (panelKey === "affiliations") inner = <AffiliationsSettingsEntry />;
          else if (panelKey === "notifications") inner = <ProviderNotificationsPanel />;
          else
            inner = (
              <>
                <ProviderPaymentsPanel />
                <ConsultationPaymentsList />
              </>
            );
          return (
            <div className="space-y-6">
              {inner}
              <AccountSecurityBanner />
            </div>
          );
        }}
      />
    </div>
  );
}

// ─── Notifications panel ────────────────────────────────────────────────────

// The provider-scope matrix (booking requests, team, calendar sync, payments…)
// — the same one /account/provider/settings/notifications renders. The
// patient-side reminders live in the personal settings, linked below.
function ProviderNotificationsPanel() {
  return (
    <div className="space-y-4">
      <NotificationSettingsClient
        scope="provider"
        categories={PROVIDER_CATEGORIES}
        hideUserOnlyFeatures={true}
      />
      <Link
        to="/account/user/settings"
        search={{ tab: "notifications" }}
        className="text-primary inline-flex min-h-11 items-center text-sm font-medium underline-offset-4 hover:underline"
      >
        <Trans>Your reminders as a patient →</Trans>
      </Link>
    </div>
  );
}

// ─── Booking policy panel ───────────────────────────────────────────────────

interface BookingPolicy {
  noShowThreshold: number;
  noShowAction: "warn" | "require_prepayment" | "block_booking";
  lateCancellationWindowHours: number;
  lateCancellationFeePercent: number;
}

const POLICY_KEY = ["api", "/api/account/provider/settings/booking-policy"] as const;
const WINDOW_OPTIONS = [6, 12, 24, 48] as const;
const FEE_OPTIONS = [0, 25, 50, 100] as const;

function BookingPolicyPanel() {
  const { t, i18n } = useLingui();
  const errorMessage = useApiErrorMessage();
  const currency = useProviderCurrency();
  const [policy, setPolicy] = useState<BookingPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    apiFetch
      .get<BookingPolicy>(POLICY_KEY[1])
      .then((data) => {
        if (cancelled) return;
        setPolicy(data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          const body = err.body as { error?: string } | null;
          if (body?.error === "TOTP_CHALLENGE_REQUIRED") {
            setErrored(true);
            setLoading(false);
            return;
          }
        }
        setErrored(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const update = <K extends keyof BookingPolicy>(key: K, value: BookingPolicy[K]) => {
    setPolicy((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!policy) return;
    setSaving(true);
    const toast = showToast(t`Saving…`, "loading");
    try {
      const updated = await apiFetch.put<BookingPolicy>(POLICY_KEY[1], policy);
      setPolicy(updated);
      toast.update(t`Saved`, "success");
    } catch (err) {
      toast.update(errorMessage(err, t`Could not save changes`), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PolicySkeleton />;
  if (errored)
    return (
      <LoadErrorState
        title={<Trans>Could not load booking policy.</Trans>}
        onRetry={() => {
          setLoading(true);
          setErrored(false);
          setReloadKey((k) => k + 1);
        }}
      />
    );
  if (!policy) return null;

  // Worked example in the provider's own currency (never a fixed "RON").
  const EXAMPLE_PRICE = 200;
  const exampleFee = policy.lateCancellationFeePercent
    ? Math.round(EXAMPLE_PRICE * (policy.lateCancellationFeePercent / 100))
    : 0;
  const examplePrice = formatMoney(EXAMPLE_PRICE, currency, i18n.locale, {
    maximumFractionDigits: 0,
  });
  const exampleFeeLabel = formatMoney(exampleFee, currency, i18n.locale, {
    maximumFractionDigits: 0,
  });

  return (
    <div className="space-y-6 pb-6">
      <m.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="border-border bg-card space-y-5 rounded-xl border p-6"
      >
        <div>
          <h2 className="text-foreground text-base font-semibold">
            <Trans>Late cancellation</Trans>
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            <Trans>How close to the appointment patients can cancel free of charge.</Trans>
          </p>
        </div>
        <div>
          <p className="text-foreground mb-2 text-sm font-medium">
            <Trans>Cancellation window</Trans>
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {WINDOW_OPTIONS.map((h) => {
              const active = policy.lateCancellationWindowHours === h;
              return (
                <button
                  key={h}
                  type="button"
                  onClick={() => update("lateCancellationWindowHours", h)}
                  className={cn(
                    "min-h-[44px] rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors",
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-card text-foreground hover:bg-muted",
                  )}
                >
                  {h}h
                </button>
              );
            })}
          </div>
          <p className="text-muted-foreground mt-2 text-xs">
            <Trans>
              Cancellations later than this trigger the fee below; earlier cancellations are always
              free.
            </Trans>
          </p>
        </div>
        <CancellationTimeline windowHours={policy.lateCancellationWindowHours} />
      </m.section>

      <m.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.05 }}
        className="border-border bg-card space-y-5 rounded-xl border p-6"
      >
        <div>
          <h2 className="text-foreground text-base font-semibold">
            <Trans>Cancellation fee</Trans>
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            <Trans>Percent of the consultation price charged on late cancellations.</Trans>
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {FEE_OPTIONS.map((pct) => {
            const active = policy.lateCancellationFeePercent === pct;
            return (
              <button
                key={pct}
                type="button"
                onClick={() => update("lateCancellationFeePercent", pct)}
                className={cn(
                  "min-h-[44px] rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors",
                  active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-foreground hover:bg-muted",
                )}
              >
                {pct === 0 ? <Trans>Free</Trans> : `${pct}%`}
              </button>
            );
          })}
        </div>
        <AnimatePresence mode="wait">
          <m.div
            key={exampleFee}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="bg-muted/60 rounded-lg px-4 py-3"
          >
            <p className="text-muted-foreground mb-1 text-xs font-medium">
              <Trans>Example: a {examplePrice} consultation</Trans>
            </p>
            {exampleFee > 0 ? (
              <p className="text-foreground text-sm font-semibold">
                <Trans>Late cancellation fee: {exampleFeeLabel}</Trans>
              </p>
            ) : (
              <p className="text-success-strong text-sm font-semibold">
                <Trans>No fee charged.</Trans>
              </p>
            )}
          </m.div>
        </AnimatePresence>
      </m.section>

      <m.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.1 }}
        className="border-border bg-card space-y-5 rounded-xl border p-6"
      >
        <div>
          <h2 className="text-foreground text-base font-semibold">
            <Trans>No-show policy</Trans>
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            <Trans>What happens after a patient skips appointments without notice.</Trans>
          </p>
        </div>
        <div>
          <p className="text-foreground mb-3 text-sm font-medium">
            <Trans>Allowed no-shows before action</Trans>
          </p>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => update("noShowThreshold", Math.max(1, policy.noShowThreshold - 1))}
              disabled={policy.noShowThreshold <= 1}
              className={cn(
                "border-border flex size-11 items-center justify-center rounded-lg border transition-all",
                policy.noShowThreshold <= 1
                  ? "text-muted-foreground cursor-not-allowed opacity-50"
                  : "text-foreground hover:border-primary hover:text-primary",
              )}
              aria-label={t`Decrease threshold`}
            >
              <Minus className="size-4" />
            </button>
            <div className="flex min-w-[3rem] flex-col items-center">
              <span className="text-foreground text-3xl font-bold tabular-nums">
                {policy.noShowThreshold}
              </span>
              <span className="text-muted-foreground mt-0.5 text-xs">
                {policy.noShowThreshold === 1 ? (
                  <Trans>1 no-show</Trans>
                ) : (
                  <Trans>{policy.noShowThreshold} no-shows</Trans>
                )}
              </span>
            </div>
            <button
              type="button"
              onClick={() => update("noShowThreshold", Math.min(10, policy.noShowThreshold + 1))}
              disabled={policy.noShowThreshold >= 10}
              className={cn(
                "border-border flex size-11 items-center justify-center rounded-lg border transition-all",
                policy.noShowThreshold >= 10
                  ? "text-muted-foreground cursor-not-allowed opacity-50"
                  : "text-foreground hover:border-primary hover:text-primary",
              )}
              aria-label={t`Increase threshold`}
            >
              <Plus className="size-4" />
            </button>
          </div>
        </div>
        <div>
          <p className="text-foreground mb-3 text-sm font-medium">
            <Trans>Action when threshold reached</Trans>
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <NoShowActionCard
              value="warn"
              active={policy.noShowAction === "warn"}
              icon={AlertTriangle}
              activeBg="border-amber-400 bg-amber-50 dark:bg-amber-900/20"
              iconColor="text-amber-600 dark:text-amber-400"
              title={<Trans>Warn patient</Trans>}
              desc={<Trans>Show a notice on next booking; no other restrictions.</Trans>}
              onClick={() => update("noShowAction", "warn")}
            />
            <NoShowActionCard
              value="require_prepayment"
              active={policy.noShowAction === "require_prepayment"}
              icon={CreditCard}
              activeBg="border-blue-400 bg-blue-50 dark:bg-blue-900/20"
              iconColor="text-blue-600 dark:text-blue-400"
              title={<Trans>Require prepayment</Trans>}
              desc={<Trans>Patient must pay upfront when booking again.</Trans>}
              onClick={() => update("noShowAction", "require_prepayment")}
            />
            <NoShowActionCard
              value="block_booking"
              active={policy.noShowAction === "block_booking"}
              icon={Ban}
              activeBg="border-red-400 bg-red-50 dark:bg-red-900/20"
              iconColor="text-red-600 dark:text-red-400"
              title={<Trans>Block booking</Trans>}
              desc={<Trans>Patient cannot book again until you unblock them.</Trans>}
              onClick={() => update("noShowAction", "block_booking")}
            />
          </div>
        </div>
      </m.section>

      <div className="border-border bg-card flex items-center justify-end gap-3 rounded-xl border px-5 py-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="bg-primary text-primary-foreground inline-flex min-h-[44px] min-w-[140px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              <Trans>Saving…</Trans>
            </>
          ) : (
            <Trans>Save changes</Trans>
          )}
        </button>
      </div>
    </div>
  );
}

function NoShowActionCard({
  active,
  icon: Icon,
  activeBg,
  iconColor,
  title,
  desc,
  onClick,
}: {
  value: BookingPolicy["noShowAction"];
  active: boolean;
  icon: typeof AlertTriangle;
  activeBg: string;
  iconColor: string;
  title: React.ReactNode;
  desc: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex min-h-[44px] flex-col items-start gap-1.5 rounded-lg border p-3 text-left transition-colors",
        active ? activeBg : "border-border bg-card hover:bg-muted",
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className={cn("size-4", active ? iconColor : "text-muted-foreground")} />
        <span className="text-foreground text-sm font-medium">{title}</span>
      </div>
      <p className="text-muted-foreground text-xs leading-snug">{desc}</p>
    </button>
  );
}

function CancellationTimeline({ windowHours }: { windowHours: number }) {
  const freePercent = Math.min(85, Math.max(15, (windowHours / 72) * 100));
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-xs font-medium uppercase">
        <Trans>Timeline</Trans>
      </p>
      <div className="relative flex h-8 w-full overflow-hidden rounded-full">
        <div
          className="flex items-center justify-center bg-emerald-100 text-xs font-medium text-emerald-700 transition-all duration-300 dark:bg-emerald-900/30 dark:text-emerald-400"
          style={{ width: `${100 - freePercent}%` }}
        >
          <span className="truncate px-2">
            <Trans>Free</Trans>
          </span>
        </div>
        <div
          className="flex items-center justify-center bg-amber-100 text-xs font-medium text-amber-700 transition-all duration-300 dark:bg-amber-900/30 dark:text-amber-400"
          style={{ width: `${freePercent}%` }}
        >
          <span className="truncate px-2">
            <Trans>Late zone</Trans>
          </span>
        </div>
      </div>
      <div className="text-muted-foreground flex justify-between text-xs">
        <span>
          <Trans>Now</Trans>
        </span>
        <span className="font-medium text-amber-600 dark:text-amber-400">−{windowHours}h</span>
        <span>
          <Trans>Appointment</Trans>
        </span>
      </div>
    </div>
  );
}

function PolicySkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="border-border bg-card animate-pulse space-y-4 rounded-xl border p-6"
        >
          <div className="bg-muted h-5 w-40 rounded" />
          <div className="bg-muted h-4 w-64 rounded" />
          <div className="flex gap-3 pt-2">
            {[1, 2, 3].map((j) => (
              <div key={j} className="bg-muted h-12 flex-1 rounded-lg" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
